import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "../context/StoreContext"; // ✅ Use StoreContext
import { getImageUrl } from "../utility/imageHelper"; // ✅ Import helper for image URL
import Products from "../components/Products"; // ✅ Use modular Products component
import "./styles/ViewCategory.css";

import defaultImage from './../assets/allProducts.webp';
import SEO from "../seo/SEO";
import config from "../config/config";
import { slugify } from "../utility/slugify";
import generateBreadcrumbs from "../utility/generateBreadcrumbs";

const ViewCategory = () => {
  const { categorySlug } = useParams();
  const { products, categories } = useStore(); // ✅ Get categories from StoreContext
  const [category, setCategory] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(true); // ✅ Track if image loads

  useEffect(() => {
    if (categories.length && products.length) {
      const matchedCategory = categories.find(
        (cat) => cat.name.toLowerCase().replace(/\s+/g, "-") === categorySlug
      );
      setCategory(matchedCategory || null);
    }
  }, [categories, products, categorySlug]);
  
  // ✅ Filter products using category ID instead of category name
  const filteredProducts = category
    ? products.filter((product) => product.category_id === category.id)
    : [];

  // ✅ Handle image load failure (fallback to default)
  useEffect(() => {
    if (category?.image_url) {
      const img = new Image();
      img.src = getImageUrl(category.image_url);
      img.onload = () => setImageLoaded(true); // ✅ Image loads successfully
      img.onerror = () => setImageLoaded(false); // ❌ Image fails, use default
    } else {
      setImageLoaded(false); // No image set, use default
    }
  }, [category]);

  // ✅ Determine the background image
  const backgroundImage = imageLoaded && category?.image_url
    ? `url(${getImageUrl(category.image_url)})`
    : `url(${defaultImage})`;


  return (
    <>
    
          <SEO
              title={category ? `Shop ${category.name} - Withrow Knives` : "Category Not Found"}
              description={
                  category
                      ? `Browse our collection of ${category.name.toLowerCase()} at Withrow Knives. Handmade custom knives with expert craftsmanship.`
                      : "The category you are looking for does not exist."
              }
              keywords={
                  category
                      ? [
                          `${category.name.toLowerCase()} knives`,
                          `handmade ${category.name.toLowerCase()} knives`,
                          `Withrow Knives ${category.name.toLowerCase()}`
                        ]
                      : ["custom knives", "handmade knives", "premium knives", "Withrow Knives"]
              }
              url={`${config.metadata.siteUrl}/shop/category/${categorySlug}`}
              image={category?.image_url ? getImageUrl(category.image_url) : undefined}
              jsonLd={
                category && filteredProducts.length > 0
                    ? {
                          "@context": "https://schema.org",
                          "@type": "ItemList",
                          "name": `Shop ${category.name} - Withrow Knives`,
                          "url": `${config.metadata.siteUrl}/shop-knives/category/${categorySlug}`,
                          "description": `Explore our collection of handcrafted ${category.name.toLowerCase()} knives, designed with expert craftsmanship.`,
                          "mainEntityOfPage": {
                              "@type": "WebPage",
                              "@id": `${config.metadata.siteUrl}/shop-knives/category/${categorySlug}`
                          },
                          "itemListOrder": "https://schema.org/ItemListOrderDescending",
                          "numberOfItems": filteredProducts.length,
                          "itemListElement": filteredProducts.map((product, index) => ({
                              "@type": "ListItem",
                              "position": index + 1,
                              "name": product.name,
                              "url": `${config.metadata.siteUrl}/shop-knives/knife/${product.id}/${slugify(product.name)}`,
                              "image": product.images ? product.images.map(img => getImageUrl(img)) : [],
                              "offers": {
                                  "@type": "Offer",
                                  "priceCurrency": "USD",
                                  "price": product.price,
                                  "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                  "seller": {
                                    "@type": "Store",
                                    "name": "Withrow Knives"
                                  }
                              }
                          })),
                          "breadcrumb": generateBreadcrumbs(["shop-knives", "category"], null, category)
                      }
                    : undefined
            }
          />
        
      
        <div className="page-wrapper">
          {/* ✅ Category Page Title with Background Image */}
          <div
            className="page-title"
            style={{
              backgroundImage: backgroundImage,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <h1>Shop {category?.name || categorySlug.replace(/-/g, " ")}</h1>
            <p className="page-subtitle">
              Browse our selection of {category?.name?.toLowerCase() || categorySlug.replace(/-/g, " ")}, expertly crafted for durability and performance.
            </p>
          </div>

          {/* ✅ Loading State with Accessible Announcements */}
          {category ? (
            /* ✅ Product Listings */
            <Products products={filteredProducts} listName={category?.name} fireListViewEvent={true} />
          ) : (
            /* ✅ Accessible "Category Not Found" Message */
            <p className="no-category" role="alert" aria-live="assertive">
              Category Not Found
            </p>
          )}
        </div>
    </>
  );
};

export default ViewCategory;
